import Base from "../base";

import { ColumnCell } from "../../types/data-table";
import { useEffect, useMemo, useState } from "react";
import { request } from "../../utils";
import { ControlUnion, DateRangeRuntime } from "../../types/control-bar";
import useQuerySettings from "../../hooks/use-query-settings";
import ControlBar from "../../components/control-bar";
import { BaseRuntime } from "../../types/common";
import styled from "styled-components";
import useRemoteActionProps from "../../hooks/use-remote-action-props";
import ActionTable from "../../components/action-table";
import { VerticalSeparator } from "../../components/separators";
import { useAppSelector } from "../../state/hooks";
import Dropdown, { DropdownOption } from "../../components/inputs/dropdown";
import DailySnapshot from "./daily-snapshot";
import { downloadPdf } from "../../utils/download-pdf";
import useTableActions from "../../hooks/use-table-actions";
import { CAUCUS, PRESIDENTIAL } from "../../data/constants";
import states from "../../data/states.json";
import Select from "react-select";

const stateOptions = [
	{ value: "", label: "(all)" },
	...states.map(
		s =>
			({
				value: s.state,
				label: s.state
			} as DropdownOption)
	)
];

const presidentialColumns: ColumnCell[] = PRESIDENTIAL
	? [
			{
				name: "state",
				title: "State",
				accessor: "state",
				index: "lexical",
				fit: "shrink"
			}
	  ]
	: [];

const COLUMNS: ColumnCell[] = [
	{
		name: "race",
		title: "Race",
		accessor: "race_name",
		index: "lexical"
	},
	{
		name: "recipient",
		title: "Recipient",
		accessor: "email_list_name",
		index: "lexical"
	},
	...presidentialColumns
];

const runtime = {
	state: {}
} as BaseRuntime;

const PreviewHeader = styled.div`
	display: flex;
	justify-content: flex-start;
	align-items: center;
	padding: 15px;
	background: ${p => p.theme.componentBackground};
	border-radius: ${p => p.theme.borderRadius};
	font-weight: bold;
`;

const HeaderSpacer = styled.div`
	position: relative;
	height: 100%;
	margin-right: 1rem;
`;

const SnapshotsWrapper = styled.div`
	display: flex;
	flex-grow: 1;
	padding: 15px;
	background: ${p => p.theme.cardBackground};
	border-radius: ${p => p.theme.borderRadius};
	overflow: hidden;
`;

interface SnapshotProps {
	raceId: string;
	startDate: number;
	endDate: number;
}

type SelectOption = {
	label: string;
	value: string;
};

const Content = () => {
	const { agencies, races } = useAppSelector(state => state.data);
	const [raceIds, setRaceIds] = useState<string[]>([]);
	const [selectedState, setSelectedState] = useState<
		string | undefined
	>();
	const [updatePending, setUpdatePending] = useState(false);
	const [flightDateRange, setFlightDateRange] =
		useState<[number, number]>();
	const { querySettings, setDateRange } = useQuerySettings();

	const [campaignOptions, setCampaignOptions] = useState<SelectOption[]>(
		[]
	);
	const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>(
		[]
	);

	const [selectedAgency, setSelectedAgency] = useState<string>();

	const agencyOptions = useMemo<SelectOption[]>(
		() => [
			{ label: "<none>", value: "" },
			...agencies.map(a => ({
				label: a.full_name,
				value: a.id.toString()
			}))
		],
		[agencies]
	);

	useEffect(() => {
		if (raceIds) {
			setUpdatePending(true);

			request({
				query: {
					race_id: raceIds
				},
				url: "flights/list"
			})
				.then(({ data }) => {
					if (
						!Array.isArray(data) ||
						!data.length
					)
						return;
					const dateRange = data.reduce(
						(dateRange, flight) => {
							try {
								const flightStartTime =
									new Date(
										flight.start_date
									).getTime();
								const flightEndTime =
									new Date(
										flight.end_date
									).getTime() +
									24 *
										3600 *
										1000; // add 1 day

								const start =
									dateRange[0]
										? Math.min(
												flightStartTime,
												dateRange[0]
										  )
										: flightStartTime;
								const end =
									dateRange[1]
										? Math.max(
												flightEndTime,
												dateRange[1]
										  )
										: flightEndTime;
								if (
									start !==
										dateRange[0] ||
									end !==
										dateRange[1]
								) {
									return [
										start,
										end
									];
								}
							} catch {} // ignore bad data
							return dateRange;
						},
						[]
					);

					if (dateRange.length) {
						setFlightDateRange(dateRange);
					}
				})
				.finally(() => setUpdatePending(false));
		}
	}, [raceIds]);

	useEffect(() => {
		request({
			url: "/campaigns/list",
			query: {
				race_id: raceIds
			}
		}).then(campaignsResponse => {
			if (!campaignsResponse.success) {
				setCampaignOptions([]);
				return;
			}
			const campaigns: SelectOption[] = Array.isArray(
				campaignsResponse.data?.campaigns
			)
				? campaignsResponse.data.campaigns.map(
						(c: any) => ({
							label: c.candidate
								.full_name,
							value: c.id
						})
				  )
				: [];
			campaigns.sort((a, b) =>
				a.label.localeCompare(b.label)
			);
			setCampaignOptions(campaigns);
			setSelectedCampaigns([]);
		});
	}, [raceIds]);

	const controls = [
		{
			label: "Snapshot",
			disabled: !raceIds || updatePending,
			onClick: () => {
				const raceName = races
					.filter(r =>
						raceIds.includes(
							r.id.toString()
						)
					)
					.map(r => r.race_name)
					.join(", ");
				const filename = raceName
					? raceName + " Snapshot"
					: "Snapshot";
				downloadPdf(filename);
			}
		},
		{
			label: "\u{1F50D}",
			disabled: !flightDateRange,
			onClick: () => {
				if (flightDateRange)
					setDateRange(flightDateRange);
			}
		},
		{
			mode: "date-range",
			value: querySettings.dateRange,
			onChange: (rt: DateRangeRuntime) => {
				setDateRange(rt.range);
			}
		}
	] as ControlUnion[];
	const raceOptions: DropdownOption[] = useMemo(() => {
		const sortedRaces = [...races].sort((a, b) =>
			a.race_name.localeCompare(b.race_name)
		);
		if (!CAUCUS && sortedRaces[0]) {
			setRaceIds([sortedRaces[0].id.toString()]);
		}
		return sortedRaces.map(race => ({
			value: race.id.toString(),
			label: race.race_name
		}));
	}, [races]);

	function isOption(
		o: DropdownOption
	): o is { value: string; label: string } {
		return (
			typeof o === "object" &&
			o !== null &&
			"value" in o &&
			"label" in o
		);
	}

	return (
		<div style={{ overflow: "auto", width: "50%" }}>
			<PreviewHeader>
				<span>Snapshot Preview for Race</span>
				<HeaderSpacer />
				{!CAUCUS && (
					<Dropdown
						value={raceIds[0]}
						options={raceOptions}
						name="Race"
						onChange={(evt: any) => {
							const raw =
								evt?.target
									?.value;
							const raceId =
								typeof raw ===
								"string"
									? raw
									: raw?.toString?.();

							if (
								raceId &&
								!isNaN(
									Number(
										raceId
									)
								)
							) {
								setRaceIds([
									raceId
								]);
							}
						}}
					/>
				)}
				{PRESIDENTIAL && (
					<Dropdown
						value={selectedState || ""}
						options={stateOptions}
						name="State"
						onChange={(evt: any) => {
							const state =
								evt?.target
									?.value;
							setSelectedState(state);
						}}
					/>
				)}
				{CAUCUS && (
					<Select
						isMulti
						name="Races"
						options={raceOptions.filter(
							isOption
						)}
						value={raceOptions
							.filter(isOption)
							.filter(o =>
								raceIds.includes(
									o.value
								)
							)}
						onChange={(selected: any) => {
							if (
								Array.isArray(
									selected
								)
							) {
								setRaceIds(
									selected.map(
										(
											s: any
										) =>
											s.value
									)
								);
							}
						}}
					/>
				)}
				<Select
					isMulti={true}
					onChange={v => {
						if (Array.isArray(v)) {
							setSelectedCampaigns(
								v.map(
									v =>
										v.value
								)
							);
						}
					}}
					placeholder="Campaigns"
					options={campaignOptions}
					value={campaignOptions.filter(c =>
						selectedCampaigns.includes(
							c.value
						)
					)}
				/>
				<Select
					onChange={v => {
						setSelectedAgency(
							v?.value || ""
						);
					}}
					placeholder="Agency"
					options={agencyOptions}
				/>
			</PreviewHeader>
			<ControlBar controls={controls} runtime={runtime} />
			{raceIds && !updatePending && (
				<DailySnapshot
					race_id={raceIds}
					start_date={
						querySettings
							.normalizedDateRange[0]
					}
					end_date={
						querySettings
							.normalizedDateRange[1]
					}
					state={selectedState}
					campaigns={
						selectedCampaigns.length > 0
							? selectedCampaigns
							: undefined
					}
					agency_id={selectedAgency}
					rollup_mode={
						CAUCUS ? "rollup" : undefined
					}
				/>
			)}
		</div>
	);
};

const Snapshots = () => {
	const actions = useTableActions({
		export: async (row: any) => {
			const queries = [
				`snapshots=full_race&race_ids=${row.race_id}`
			];
			if (row.state) {
				queries.push(`state=${row.state}`);
			}
			if (row.agency_id) {
				queries.push(`agency_id=${row.agency_id}`);
			}
			const query =
				queries.length > 0
					? `?${queries.join("&")}`
					: "";
			await request({
				url: `/cron/generate_daily_snapshot/${row.email_list_id}${query}`,
				method: "POST"
			});
		},
		exportAll: async (row: any) => {
			const queries = [
				`snapshots=all&race_ids=${row.race_id}`
			];
			if (row.state) {
				queries.push(`state=${row.state}`);
			}
			if (row.agency_id) {
				queries.push(`agency_id=${row.agency_id}`);
			}
			const query =
				queries.length > 0
					? `?${queries.join("&")}`
					: "";
			await request({
				url: `/cron/generate_daily_snapshot/${row.email_list_id}${query}`,
				method: "POST"
			});
		},
		rollUp: async (rows: any[]) => {
			const queries = [
				`race_ids=${rows.map(r => r.race_id).join(",")}`
			];
			if (rows[0].agency_id) {
				queries.push(`agency_id=${rows[0].agency_id}`);
			}

			const query =
				queries.length > 0
					? `?${queries.join("&")}`
					: "";
			const emailListIds = new Set<number>(
				rows.map(r => r.email_list_id)
			);
			if (emailListIds.size !== 1) {
				throw new Error(
					"Cannot roll up multiple email lists"
				);
			}
			const emailListId = emailListIds.values().next().value;
			await request({
				url: `/cron/generate_rollup_snapshot/${emailListId}${query}`,
				method: "POST"
			});
		}
	});

	const props = useRemoteActionProps({
		request: {
			url: "/snapshots/today"
		},
		actions,
		columns: COLUMNS,
		expand: true,
		pageSize: 50
	});

	return (
		<Base limited>
			<SnapshotsWrapper>
				<ActionTable {...props} />
				<VerticalSeparator />
				<Content />
			</SnapshotsWrapper>
		</Base>
	);
};

export default Snapshots;
